export type TimeLogFieldKey =
  | 'faceToFaceMinutes'
  | 'patientHistoryMinutes'
  | 'chartingMinutes'
  | 'labReviewMinutes'
  | 'externalRecordsMinutes'
  | 'orderingMinutes'
  | 'counselingMinutes'
  | 'careCoordinationMinutes'
  | 'preVisitContactMinutes'

export const DEFAULT_TIME_LOG_FIELD_LABELS: Record<TimeLogFieldKey, string> = {
  faceToFaceMinutes: 'Face-to-face time with patient',
  patientHistoryMinutes: 'Patient history review',
  chartingMinutes: 'Charting / documentation',
  labReviewMinutes: 'Lab / result review',
  externalRecordsMinutes: 'External records review',
  orderingMinutes: 'Ordering tests, medications, or referrals',
  counselingMinutes: 'Counseling / patient education',
  careCoordinationMinutes: 'Care coordination',
  preVisitContactMinutes: 'Pre-visit phone / video contact',
}

export const TIME_LOG_FIELD_ORDER: TimeLogFieldKey[] = [
  'faceToFaceMinutes',
  'patientHistoryMinutes',
  'chartingMinutes',
  'labReviewMinutes',
  'externalRecordsMinutes',
  'orderingMinutes',
  'counselingMinutes',
  'careCoordinationMinutes',
  'preVisitContactMinutes',
]

export interface EmThreshold {
  cptCode: string
  label: string
  minMinutes: number
  maxMinutes: number
}

export const DEFAULT_EM_THRESHOLDS: EmThreshold[] = [
  { cptCode: '99212', label: 'Straightforward', minMinutes: 10, maxMinutes: 19 },
  { cptCode: '99213', label: 'Low', minMinutes: 20, maxMinutes: 29 },
  { cptCode: '99214', label: 'Moderate', minMinutes: 30, maxMinutes: 39 },
  { cptCode: '99215', label: 'High', minMinutes: 40, maxMinutes: 54 },
]

// §4.3: Intrapartum time thresholds for AMA L&D code mapping.
// Stored in Firestore so Hamilton Billing can update without a code deploy
// when AMA revises L&D codes (expected September 2026).
export interface IntrapartumThreshold {
  cptCode: string
  label: string
  minMinutes: number
  maxMinutes: number
}

// Placeholder defaults — to be updated by admin when AMA September 2026 codes publish.
export const DEFAULT_INTRAPARTUM_THRESHOLDS: IntrapartumThreshold[] = [
  { cptCode: '59400', label: 'Global OB care (placeholder)', minMinutes: 0, maxMinutes: 9999 },
]

// §5.2: Delivery time log fields — all AMA thresholds configurable by admin.
// AMA delivery code revisions expected September 2026.
export type DeliveryTimeLogFieldKey =
  | 'activeManagementMinutes'
  | 'deliveryAttendanceMinutes'
  | 'lacerationRepairMinutes'
  | 'immediatePostpartumMinutes'
  | 'newbornAssessmentMinutes'
  | 'documentationMinutes'

export const DEFAULT_DELIVERY_TIME_LOG_LABELS: Record<DeliveryTimeLogFieldKey, string> = {
  activeManagementMinutes: 'Active management of labor (attendance)',
  deliveryAttendanceMinutes: 'Delivery attendance',
  lacerationRepairMinutes: 'Laceration repair',
  immediatePostpartumMinutes: 'Immediate postpartum management',
  newbornAssessmentMinutes: 'Newborn assessment',
  documentationMinutes: 'Documentation',
}

export const DELIVERY_TIME_LOG_FIELD_ORDER: DeliveryTimeLogFieldKey[] = [
  'activeManagementMinutes',
  'deliveryAttendanceMinutes',
  'lacerationRepairMinutes',
  'immediatePostpartumMinutes',
  'newbornAssessmentMinutes',
  'documentationMinutes',
]

export interface DeliveryTimeThreshold {
  cptCode: string
  label: string
  minMinutes: number
  maxMinutes: number
}

// Placeholder defaults — to be updated by admin when AMA September 2026 delivery codes publish.
export const DEFAULT_DELIVERY_TIME_THRESHOLDS: DeliveryTimeThreshold[] = [
  {
    cptCode: '59400',
    label: 'Global OB (delivery — placeholder)',
    minMinutes: 0,
    maxMinutes: 9999,
  },
]

export interface TimeLogConfig {
  fieldLabels: Partial<Record<TimeLogFieldKey, string>>
  emThresholds: EmThreshold[]
  /** §4.3: Intrapartum attendance time thresholds for AMA L&D code mapping */
  intrapartumThresholds?: IntrapartumThreshold[]
  /** §5.2: Delivery total service time thresholds for AMA delivery code mapping */
  deliveryTimeThresholds?: DeliveryTimeThreshold[]
  /** §5.2: Admin-configurable labels for delivery time log fields */
  deliveryTimeLogLabels?: Partial<Record<DeliveryTimeLogFieldKey, string>>
  updatedOn?: number
  updatedBy?: string
}
